/**
 * تحويل نقطة بإحداثيّات «نسبة من الصورة الطبيعيّة» إلى موقع CSS
 * داخل حاوية تستعمل object-fit: cover + object-position.
 *
 * يُستعمل في المحرّر (object-contain → لا حاجة لتحويل، نسبة الصورة = نسبة الحاوية)
 * وفي العرض الحيّ (object-cover بأيّ حاوية → نحسب الكروب فعلياً).
 */

export interface CoverMapInput {
  /** نسبة من عرض الصورة الطبيعيّة 0..100 */
  xImgPct: number;
  /** نسبة من ارتفاع الصورة الطبيعيّة 0..100 */
  yImgPct: number;
  /** عرض الحاوية بالبكسل */
  containerW: number;
  /** ارتفاع الحاوية بالبكسل */
  containerH: number;
  /** عرض الصورة الطبيعيّ */
  imgW: number;
  /** ارتفاع الصورة الطبيعيّ */
  imgH: number;
  /** object-position الأفقي (٪) — افتراضي 50 */
  posX?: number;
  /** object-position الرأسي (٪) — افتراضي 50 */
  posY?: number;
  /** "cover" (افتراضي) أو "contain" */
  fit?: "cover" | "contain";
}

export interface CoverMapOutput {
  /** يسار النقطة بالنسبة للحاوية (٪) */
  leftPct: number;
  /** أعلى النقطة بالنسبة للحاوية (٪) */
  topPct: number;
  /** هل النقطة داخل المنطقة المرئيّة بعد الكروب */
  visible: boolean;
}

export function mapImagePctToContainer({
  xImgPct,
  yImgPct,
  containerW,
  containerH,
  imgW,
  imgH,
  posX = 50,
  posY = 50,
  fit = "cover",
}: CoverMapInput): CoverMapOutput {
  if (!containerW || !containerH || !imgW || !imgH) {
    // قبل تحميل الصورة — نرجع موقع تقريبيّ كأنّه نسبة من الحاوية
    return { leftPct: xImgPct, topPct: yImgPct, visible: true };
  }

  const scale =
    fit === "cover"
      ? Math.max(containerW / imgW, containerH / imgH)
      : Math.min(containerW / imgW, containerH / imgH);

  const renderedW = imgW * scale;
  const renderedH = imgH * scale;

  // الفرق بين عرض الصورة المرسومة والحاوية يُوزَّع وفق object-position
  const offsetX = (containerW - renderedW) * (posX / 100);
  const offsetY = (containerH - renderedH) * (posY / 100);

  // موقع النقطة داخل الصورة المرسومة، ثمّ نضيف الإزاحة
  const xPx = offsetX + (xImgPct / 100) * renderedW;
  const yPx = offsetY + (yImgPct / 100) * renderedH;

  const leftPct = (xPx / containerW) * 100;
  const topPct = (yPx / containerH) * 100;

  const visible =
    leftPct >= 0 && leftPct <= 100 && topPct >= 0 && topPct <= 100;

  return { leftPct, topPct, visible };
}

/**
 * عكسيّ: من نقرة المستخدم داخل حاوية (٪ من الحاوية)
 * → إلى نسبة من الصورة الطبيعيّة (للحفظ).
 * في حالة contain، الصورة تظهر كاملة فلا حاجة لكروب → النسبة كما هي
 * لو نسبة الحاوية = نسبة الصورة. وإلّا نحسب الإزاحة.
 */
export function mapContainerPctToImage({
  leftPct,
  topPct,
  containerW,
  containerH,
  imgW,
  imgH,
  posX = 50,
  posY = 50,
  fit = "contain",
}: {
  leftPct: number;
  topPct: number;
  containerW: number;
  containerH: number;
  imgW: number;
  imgH: number;
  posX?: number;
  posY?: number;
  fit?: "cover" | "contain";
}): { xImgPct: number; yImgPct: number; insideImage: boolean } {
  if (!containerW || !containerH || !imgW || !imgH) {
    return { xImgPct: leftPct, yImgPct: topPct, insideImage: true };
  }
  const scale =
    fit === "cover"
      ? Math.max(containerW / imgW, containerH / imgH)
      : Math.min(containerW / imgW, containerH / imgH);
  const renderedW = imgW * scale;
  const renderedH = imgH * scale;
  const offsetX = (containerW - renderedW) * (posX / 100);
  const offsetY = (containerH - renderedH) * (posY / 100);

  const xPx = (leftPct / 100) * containerW;
  const yPx = (topPct / 100) * containerH;

  const xInImg = xPx - offsetX;
  const yInImg = yPx - offsetY;

  const xImgPct = (xInImg / renderedW) * 100;
  const yImgPct = (yInImg / renderedH) * 100;

  const insideImage =
    xImgPct >= 0 && xImgPct <= 100 && yImgPct >= 0 && yImgPct <= 100;

  return {
    xImgPct: Math.max(0, Math.min(100, xImgPct)),
    yImgPct: Math.max(0, Math.min(100, yImgPct)),
    insideImage,
  };
}
